import React, { useState, useMemo, ReactNode, useCallback, useEffect } from "react"
import { Typography, Box, Button, Grid, Paper, Dialog, DialogTitle } from "@material-ui/core"
import { Skeleton } from "@material-ui/lab"
import { makeStyles, Theme } from "@material-ui/core/styles"
import { Localized, useLocalization } from "@fluent/react"
import { LocalizedStrict } from "shared/Modules/Localization/Components/Localized"
import { FormInputField, InputType, ValidationType } from "shared/Modules/Forms/formTypes"
import { DynamicFormField } from "shared/Modules/Forms/Components/DynamicFormField"
import { Form, Formik } from "formik"
import LoadingSpinner from "shared/Modules/Query/Components/LoadingSpinner"
import clsx from "clsx"
import { SafeArea } from "../Skeleton/SafeArea"

type StyleProps = {
    termsFloatingBottom?: string
}

const useIframeStyles = makeStyles((theme) => ({
    termsIframe: {
        width: "100%",
        height: "100%",
        backgroundColor: "white",
        border: "none",
    },
    iframeHeader: {
        width: "100%",
        padding: "0.5rem 0",
        backgroundColor: theme.palette.info.light,
        display: "flex",
        alignItems: "center",
    },
    iframeHeaderDoneButton: {
        fontSize: "0.813rem",
        "&.MuiButtonBase-root:hover": {
            backgroundColor: "transparent",
        },
        color: "white",
    },
    iframeOverlay: {
        width: "100%",
        height: "100%",
    },
    dialogTitle: {
        padding: 0,
    },
    safeAreaContainer: {
        display: "flex",
        flexDirection: "column",
        flex: 1,
    },
}))

const useStyles = makeStyles<Theme, StyleProps>((theme) => ({
    acceptTerms: (props) => ({
        position: !!props?.termsFloatingBottom ? "fixed" : "relative",
        width: "calc(100% - 2rem)",
        bottom: props?.termsFloatingBottom,
    }),
    conditionsLink: {
        cursor: "pointer",
        color: theme.palette.primary.main,
        "&:hover": {
            textDecoration: "underline",
        },
    },
    skeletons: {
        padding: theme.spacing(2),
    },
}))

type TermsIframeOverlayProps = Readonly<{
    iframeUrl: string
    onClose: () => void
}>

function TermsIframeOverlay({ iframeUrl, onClose }: TermsIframeOverlayProps) {
    const classes = useIframeStyles()
    const [loadingIframe, setLoadingIframe] = useState(true)

    return (
        <Dialog fullScreen open={!!iframeUrl}>
            <SafeArea fullHeight left right bottom className={classes.safeAreaContainer}>
                <DialogTitle className={classes.dialogTitle}>
                    <Box className={classes.iframeHeader}>
                        <SafeArea top>
                            <Localized id="paymentcards-conditions-iframe-done-button">
                                <Button
                                    variant="text"
                                    className={classes.iframeHeaderDoneButton}
                                    onClick={() => {
                                        onClose()
                                        setLoadingIframe(false)
                                    }}
                                >
                                    Done
                                </Button>
                            </Localized>
                        </SafeArea>
                    </Box>
                </DialogTitle>
                {loadingIframe && (
                    <Grid container justifyContent="flex-start" alignItems="center" className={classes.iframeOverlay}>
                        <LoadingSpinner centerVertical />
                    </Grid>
                )}
                <iframe onLoad={() => setLoadingIframe(false)} className={classes.termsIframe} src={iframeUrl}>
                    loader
                </iframe>
            </SafeArea>
        </Dialog>
    )
}

type UseTermsFieldConfig = Readonly<{
    name?: string
    termsUrl: string | undefined | null
}>

export function useTermsField({ name = "termsAccepted", termsUrl }: UseTermsFieldConfig) {
    const { l10n } = useLocalization()
    const classes = useStyles({})
    const [iframeUrl, setIframeUrl] = useState<string>("")

    const field = useMemo<FormInputField | null>(() => {
        if (!termsUrl) {
            return null
        }

        return {
            type: InputType.CHECKBOX,
            name,
            valueType: "boolean",
            initialValue: false,
            label: (
                <LocalizedStrict
                    id="accept-terms-and-conditions"
                    elems={{
                        conditionsLink: (
                            <span
                                className={classes["conditionsLink"]}
                                onClick={(e: React.MouseEvent<HTMLElement>) => {
                                    setIframeUrl(termsUrl)
                                    e.preventDefault()
                                }}
                            />
                        ),
                    }}
                >
                    <Typography variant="body2">I accept the terms and conditions</Typography>
                </LocalizedStrict>
            ),
            validations: [
                {
                    type: ValidationType.IS_TRUE,
                    message: l10n.getString("shopping-basket-conditions-terms-not-accepted"),
                },
            ],
        }
    }, [termsUrl, name, l10n, classes["conditionsLink"]])

    const closeIframe = useCallback(() => {
        setIframeUrl("")
    }, [setIframeUrl])

    return {
        field,
        iframeUrl,
        closeIframe,
    }
}

type TermsAndConditionsSectionProps = Readonly<{
    name?: string
    termsUrl: string | undefined | null
    isLoading?: boolean
    errorLabel: React.ReactNode
    termsFloatingBottom?: string
}>

export function TermsAndConditionsSection({
    name = "termsAccepted",
    termsUrl,
    isLoading = false,
    errorLabel,
    termsFloatingBottom,
}: TermsAndConditionsSectionProps) {
    const classes = useStyles({ termsFloatingBottom })
    const { field, iframeUrl, closeIframe } = useTermsField({
        name,
        termsUrl,
    })

    if (isLoading) {
        return (
            <Box className={clsx(classes["skeletons"], classes["acceptTerms"])}>
                <Skeleton variant="text" animation="wave" height={40} width="auto" />
            </Box>
        )
    }

    if (!field) {
        return (
            <Grid container justifyContent="center" alignItems="center" className={classes["acceptTerms"]}>
                <Typography variant="body2" color="error" align="center">
                    {errorLabel}
                </Typography>
            </Grid>
        )
    }

    return (
        <>
            <Grid
                container
                direction="column"
                wrap="nowrap"
                justifyContent="space-evenly"
                alignItems="flex-start"
                className={classes["acceptTerms"]}
            >
                <DynamicFormField field={field} />
            </Grid>

            {iframeUrl && <TermsIframeOverlay iframeUrl={iframeUrl} onClose={closeIframe} />}
        </>
    )
}

type TermsAndConditionsSectionWithFormProps = TermsAndConditionsSectionProps &
    Readonly<{
        onChange: (checked: boolean) => void
        enableReinitialize?: boolean
        defaultChecked?: boolean
    }>

export function TermsAndConditionsSectionWithForm({
    name = "termsAccepted",
    onChange,
    defaultChecked,
    enableReinitialize = false,
    ...props
}: TermsAndConditionsSectionWithFormProps) {
    const initialValues = {
        [name]: defaultChecked ?? false,
    }

    return (
        <Formik initialValues={initialValues} enableReinitialize={enableReinitialize} onSubmit={() => {}}>
            {({ values }) => (
                <Form onChange={() => onChange(!values[name])}>
                    <TermsAndConditionsSection name={name} {...props} />
                </Form>
            )}
        </Formik>
    )
}
